import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import clsx from 'clsx';
import { db } from '../../db/index.js';
import { loadDeckCards, buildQueue, buildTodayQueue, pickNext } from '../../scheduler/queue.js';
import { previewIntervals, Rating, RATINGS, cardStateName, formatInterval } from '../../scheduler/fsrs.js';
import { recordReview, undoReview } from '../../db/repo/reviews.js';
import { setSuspended, bury, setFlag, FLAGS } from '../../db/repo/cards.js';
import { getCardType } from '../../cardTypes/index.js';
import { isCorrect, diffHtml } from '../../cardTypes/typein.js';
import { useSettings, haptic } from '../../app/settingsStore.js';
import { useHotkeys } from '../../hooks/useHotkeys.js';
import { useNow } from '../../hooks/useNow.js';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import Sheet from '../../components/Sheet.jsx';
import { ActionList, ActionRow } from '../../components/ActionList.jsx';
import { toast } from '../../components/Toaster.jsx';
import { NoteEditor } from '../notes/NoteEditorPage.jsx';
import SwipeCard from './SwipeCard.jsx';
import RatingBar from './RatingBar.jsx';
import { readCustomSession } from './customSession.js';
import StudySetup from './StudySetup.jsx';
import styles from './Study.module.scss';

const STATE_CHIP = { new: 'New card', learning: 'Learning', review: 'Review', suspended: 'Paused' };
const ARROWS = { left: '←', right: '→', up: '↑', down: '↓' };

function Html({ html, className }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function sizeClass(html) {
  const n = (html ?? '').replace(/<[^>]+>/g, '').length;
  if (n <= 40) return styles.textBig;
  if (n > 220) return styles.textSmall;
  return undefined;
}

function WaitMessage({ until }) {
  const now = useNow(5000);
  return (
    <p>
      The next card is ready in {formatInterval(Math.max(0, until - now))}. Stay here and it will show up on its own.
    </p>
  );
}

function SwipeTip({ map, onDismiss }) {
  const parts = ['left', 'right', 'up', 'down']
    .map((dir) => ({ dir, r: RATINGS.find((x) => x.value === map[dir]) }))
    .filter((p) => p.r);
  return (
    <div className={styles.tip} role="note">
      <Icon name="hand" size={22} />
      <div className={styles.tipText}>
        <strong>Tip: swipe the card to answer</strong>
        <span>
          {parts.map((p) => (
            <span key={p.dir} className={styles.tipItem}>
              {ARROWS[p.dir]} {p.r.label}
            </span>
          ))}
        </span>
      </div>
      <Button size="sm" onClick={onDismiss}>
        Got it
      </Button>
    </div>
  );
}

export default function StudyPage() {
  const { deckId } = useParams();
  const [params] = useSearchParams();
  if (!deckId && !params.get('custom') && params.get('go') !== 'today') return <StudySetup />;
  return <StudySession />;
}

function StudySession() {
  const { deckId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const customId = params.get('custom');
  const custom = useMemo(() => (customId ? readCustomSession(customId) : null), [customId]);
  const decksQuery = params.get('decks') ?? '';
  const fromHome = params.get('from') === 'home';

  const [current, setCurrent] = useState(null); // { card, note, view, config }
  const [phase, setPhase] = useState('loading'); // loading | question | answer | waiting | finished
  const [counts, setCounts] = useState(null);
  const [waitUntil, setWaitUntil] = useState(null);
  const [typed, setTyped] = useState('');
  const [picked, setPicked] = useState([]);
  const [sheet, setSheet] = useState(null); // 'more' | 'edit'
  const [deckName, setDeckName] = useState('');
  const [answered, setAnswered] = useState(0);
  const [startedAt] = useState(() => Date.now());
  const now = useNow(30000);
  const undoStack = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const shownAt = useRef(0);
  const sessionCount = useRef(0);
  const skip = useRef(new Set());
  const customQueue = useRef(custom ? [...custom.cardIds] : null);
  const busy = useRef(false);

  const exitTo = custom?.returnTo ?? (deckId ? `/deck/${deckId}` : fromHome ? '/' : '/study');

  const show = useCallback(async (card, config) => {
    const note = await db.notes.get(card.noteId);
    if (!note || note.deletedAt) {
      skip.current.add(card.id);
      return false;
    }
    const view = getCardType(note.type).render(note, card.ord, { cardId: card.id, reps: card.fsrs?.reps ?? 0 });
    setCurrent({ card, note, view, config });
    setTyped('');
    setPicked([]);
    setPhase('question');
    shownAt.current = Date.now();
    return true;
  }, []);

  const loadNext = useCallback(async () => {
    for (let guard = 0; guard < 50; guard++) {
      const now = Date.now();
      if (customQueue.current) {
        const id = customQueue.current[0];
        setCounts({ custom: customQueue.current.length });
        if (!id) {
          setPhase('finished');
          setCurrent(null);
          return;
        }
        const card = await db.cards.get(id);
        if (!card || card.deletedAt || skip.current.has(id)) {
          customQueue.current.shift();
          continue;
        }
        const deck = await db.decks.get(card.deckId);
        if (await show(card, deck?.config)) return;
        customQueue.current.shift();
        continue;
      }
      const { cards, decks } = await loadDeckCards(db, deckId ?? null);
      const deck = deckId ? decks.find((d) => d.id === deckId) : null;
      if (deckId && (!deck || deck.deletedAt)) {
        setPhase('finished');
        return;
      }
      const rootIds = decksQuery ? decksQuery.split(',').filter(Boolean) : null;
      const names = (rootIds ?? []).map((id) => decks.find((d) => d.id === id)?.name).filter(Boolean);
      setDeckName(deck?.name ?? (names.length === 0 ? 'Today' : names.length <= 2 ? names.join(' · ') : `${names.length} decks`));
      const ready = cards.filter((c) => !skip.current.has(c.id));
      const queue = deckId
        ? buildQueue(ready, { now, config: deck?.config })
        : buildTodayQueue(decks, ready, { now, rootIds });
      setCounts(queue.counts);
      const next = pickNext(queue, { now, sessionCount: sessionCount.current });
      if (next.card) {
        const cardDeck = decks.find((d) => d.id === next.card.deckId);
        if (await show(next.card, cardDeck?.config)) return;
        continue;
      }
      setCurrent(null);
      if (next.waitUntil) {
        setWaitUntil(next.waitUntil);
        setPhase('waiting');
      } else {
        setPhase('finished');
      }
      return;
    }
    setPhase('finished');
  }, [deckId, decksQuery, show]);

  useEffect(() => {
    loadNext();
  }, [loadNext]);

  useEffect(() => {
    if (phase !== 'waiting' || !waitUntil) return undefined;
    const t = setTimeout(loadNext, Math.max(500, waitUntil - Date.now() + 200));
    return () => clearTimeout(t);
  }, [phase, waitUntil, loadNext]);

  const reveal = useCallback(() => {
    if (phase !== 'question') return;
    haptic(6);
    setPhase('answer');
  }, [phase]);

  const rate = useCallback(
    async (rating) => {
      if (phase !== 'answer' || !current || busy.current) return;
      busy.current = true;
      haptic(rating === Rating.Again ? [12, 40, 12] : 10);
      try {
        const elapsed = Date.now() - shownAt.current;
        if (customQueue.current) {
          customQueue.current.shift();
          if (custom.reschedule) {
            const logId = await recordReview(db, current.card.id, rating, elapsed);
            undoStack.current.push({ logId, cardId: current.card.id, custom: true });
          }
          if (rating === Rating.Again) customQueue.current.push(current.card.id);
        } else {
          const logId = await recordReview(db, current.card.id, rating, elapsed);
          undoStack.current.push({ logId, cardId: current.card.id });
        }
        setCanUndo(undoStack.current.length > 0);
        sessionCount.current++;
        setAnswered((n) => n + 1);
        await loadNext();
      } catch (e) {
        toast.error(`Couldn’t save your answer: ${e.message}`);
      } finally {
        busy.current = false;
      }
    },
    [phase, current, custom, loadNext],
  );

  const undo = useCallback(async () => {
    const last = undoStack.current.pop();
    setCanUndo(undoStack.current.length > 0);
    if (!last) {
      toast.info('Nothing to undo');
      return;
    }
    await undoReview(db, last.logId);
    if (customQueue.current) customQueue.current.unshift(last.cardId);
    const card = await db.cards.get(last.cardId);
    const deck = card ? await db.decks.get(card.deckId) : null;
    setAnswered((n) => Math.max(0, n - 1));
    if (card) await show(card, deck?.config);
    toast.info('Went back one card');
  }, [show]);

  const act = useCallback(
    async (kind) => {
      if (!current) return;
      setSheet(null);
      const id = current.card.id;
      if (kind === 'suspend') {
        await setSuspended(db, [id], true);
        toast.info('Card paused', { action: { label: 'Undo', onClick: () => setSuspended(db, [id], false) } });
      } else if (kind === 'bury') {
        await bury(db, [id]);
        toast.info('Skipped until tomorrow');
      } else if (kind === 'flag') {
        const next = current.card.flag === 1 ? 0 : 1;
        await setFlag(db, [id], next);
        haptic(6);
        setCurrent((c) => ({ ...c, card: { ...c.card, flag: next } }));
        return;
      }
      customQueue.current?.shift();
      skip.current.add(id);
      await loadNext();
    },
    [current, loadNext],
  );

  const view = current?.view;
  const intervals = useMemo(
    () => (current && phase === 'answer' ? previewIntervals(current.card, current.config) : null),
    [current, phase],
  );

  let suggested;
  let correctness = null;
  if (view && phase === 'answer') {
    if (view.mode === 'type') {
      correctness = isCorrect(typed, view.expected);
      suggested = correctness ? Rating.Good : Rating.Again;
    } else if (view.mode === 'mcq') {
      const right = new Set(view.choices.filter((c) => c.correct).map((c) => c.id));
      correctness = picked.length === right.size && picked.every((id) => right.has(id));
      suggested = correctness ? Rating.Good : Rating.Again;
    }
  }

  useHotkeys(
    {
      ' ': () => (phase === 'question' ? reveal() : phase === 'answer' ? rate(suggested ?? Rating.Good) : false),
      Enter: () => (phase === 'question' ? reveal() : phase === 'answer' ? rate(suggested ?? Rating.Good) : false),
      1: () => rate(Rating.Again),
      2: () => rate(Rating.Hard),
      3: () => rate(Rating.Good),
      4: () => rate(Rating.Easy),
      e: () => current && setSheet('edit'),
      s: () => act('suspend'),
      b: () => act('bury'),
      f: () => act('flag'),
      z: () => undo(),
      Escape: () => navigate(exitTo),
    },
    !sheet,
  );

  const onTypedKey = (e) => {
    if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      if (phase === 'question') reveal();
      else rate(suggested ?? Rating.Good);
    }
  };

  const pick = (id) => {
    if (phase !== 'question') return;
    haptic(5);
    if (view.multi) {
      setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    } else {
      setPicked([id]);
      setPhase('answer');
    }
  };

  const stateName = current ? cardStateName(current.card) : null;
  const flagColor = current?.card.flag ? FLAGS.find((f) => f.value === current.card.flag)?.color : null;
  const remaining = counts ? (counts.custom ?? counts.new + counts.learn + counts.review) : 0;
  const progress = answered + remaining ? answered / (answered + remaining) : 0;
  const showTip = phase === 'answer' && settings.swipeEnabled && !settings.tipsSeen.swipe;
  const minutes = Math.max(1, Math.round((now - startedAt) / 60000));

  return (
    <div className={styles.page}>
      <header className={styles.top}>
        <Button iconOnly icon="close" onClick={() => navigate(exitTo)}>
          Stop studying
        </Button>
        <div className={styles.topMid}>
          <p className={styles.deckName}>{custom?.title ?? deckName}</p>
          {settings.studyProgress ? (
            <>
              <div
                className={styles.progress}
                role="progressbar"
                aria-label="Progress"
                aria-valuemin={0}
                aria-valuemax={answered + remaining}
                aria-valuenow={answered}
              >
                <span style={{ transform: `scaleX(${progress})` }} />
              </div>
              <p className={styles.left}>{phase === 'finished' ? 'Done' : `${remaining} left`}</p>
            </>
          ) : null}
        </div>
        <Button iconOnly icon="undo" onClick={undo} disabled={!canUndo}>
          Undo last answer
        </Button>
      </header>

      {phase === 'finished' ? (
        <div className={styles.done}>
          <div className={styles.doneCard}>
            <div className={styles.stamp} aria-hidden="true">
              <Icon name="check" size={40} stroke={3} />
            </div>
            <h1>{answered ? 'Nice work!' : 'Nothing to study'}</h1>
            <p>
              {answered
                ? `You went through ${answered} card${answered === 1 ? '' : 's'} in about ${minutes} min. Recall will bring them back right before you’d forget.`
                : 'You’re all caught up here. Come back later, or practice extra if you like.'}
            </p>
            <div className={styles.doneActions}>
              <Button variant="primary" size="lg" block to={exitTo}>
                Done
              </Button>
              {deckId && !custom ? (
                <Button size="lg" block icon="bolt" to={`/deck/${deckId}?custom=1`}>
                  Extra practice
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {phase === 'waiting' ? (
        <div className={styles.done}>
          <div className={styles.doneCard}>
            <div className={clsx(styles.stamp, styles.stampWait)} aria-hidden="true">
              <Icon name="calendar" size={36} />
            </div>
            <h1>Quick break</h1>
            <WaitMessage until={waitUntil} />
            <div className={styles.doneActions}>
              <Button size="lg" block to={exitTo}>
                Leave for now
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {current && (phase === 'question' || phase === 'answer') ? (
        <div className={styles.stage}>
          <SwipeCard
            key={current.card.id + (current.card.fsrs?.reps ?? 0)}
            cardKey={current.card.id + (current.card.fsrs?.reps ?? 0)}
            revealed={phase === 'answer'}
            swipeEnabled={settings.swipeEnabled}
            swipeMap={settings.swipe}
            onReveal={view.mode === 'flip' ? reveal : () => {}}
            onSwipeRate={rate}
            flag={flagColor}
            label={settings.studyChip ? STATE_CHIP[stateName] : null}
            labelTone={stateName}
            tapHint={view.mode === 'flip' ? 'Tap to see the answer' : null}
          >
            {view.mode === 'flip' ? (
              <Html
                html={phase === 'answer' ? view.back : view.front}
                className={[sizeClass(phase === 'answer' ? view.back : view.front), phase === 'answer' && styles.answerIn]
                  .filter(Boolean)
                  .join(' ')}
              />
            ) : (
              <>
                <Html html={view.front} className={sizeClass(view.front)} />
                {view.mode === 'type' ? (
                  <div className={styles.typeBox}>
                    {phase === 'question' ? (
                      <input
                        className={styles.typeInput}
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={onTypedKey}
                        placeholder="Type your answer"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        enterKeyHint="go"
                        autoFocus
                        aria-label="Your answer"
                      />
                    ) : (
                      <div className={clsx(styles.typeResult, correctness ? styles.right : styles.wrong)}>
                        <p className={styles.resultLabel}>
                          <Icon name={correctness ? 'check' : 'close'} size={16} stroke={3} />
                          {correctness ? 'Correct' : 'Not quite'}
                        </p>
                        {correctness ? (
                          <p className={styles.expected}>{view.expected}</p>
                        ) : (
                          <>
                            <p className={styles.resultSub}>You typed</p>
                            <Html className={styles.diff} html={diffHtml(typed, view.expected) || '<em>nothing</em>'} />
                            <p className={styles.resultSub}>Correct answer</p>
                            <p className={styles.expected}>{view.expected}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                {view.mode === 'mcq' ? (
                  <ul className={styles.choices}>
                    {view.choices.map((c, i) => {
                      const chosen = picked.includes(c.id);
                      const showResult = phase === 'answer';
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            className={clsx(
                              styles.choice,
                              chosen && styles.chosen,
                              showResult && c.correct && styles.choiceRight,
                              showResult && chosen && !c.correct && styles.choiceWrong,
                            )}
                            onClick={() => pick(c.id)}
                            disabled={showResult}
                            aria-pressed={chosen}
                          >
                            <span className={styles.choiceKey}>
                              {showResult && c.correct ? (
                                <Icon name="check" size={16} stroke={3} />
                              ) : showResult && chosen ? (
                                <Icon name="close" size={16} stroke={3} />
                              ) : (
                                String.fromCharCode(65 + i)
                              )}
                            </span>
                            <span>{c.text}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {phase === 'answer' && view.back ? <Html html={`<hr class="answer-rule">${view.back}`} className={styles.answerIn} /> : null}
              </>
            )}
          </SwipeCard>

          <div className={styles.controls}>
            {showTip ? <SwipeTip map={settings.swipe} onDismiss={() => settings.seeTip('swipe')} /> : null}
            {phase === 'question' ? (
              view.mode === 'mcq' && !view.multi ? (
                <p className={styles.hint}>Tap the right answer</p>
              ) : (
                <Button variant="primary" size="lg" block onClick={reveal} disabled={view.mode === 'mcq' && !picked.length}>
                  {view.mode === 'type' ? 'Check' : view.mode === 'mcq' ? 'Check answer' : 'Show answer'}
                </Button>
              )
            ) : (
              <RatingBar
                intervals={intervals}
                onRate={rate}
                suggested={suggested}
                showIntervals={settings.showIntervals}
                buttons={settings.ratingButtons}
              />
            )}
            <div className={styles.under}>
              <button type="button" className={styles.moreBtn} onClick={() => setSheet('more')}>
                <Icon name="more" size={20} stroke={3} />
                More
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Sheet open={sheet === 'more' && !!current} onClose={() => setSheet(null)} title="This card">
        {current ? (
          <ActionList>
            <ActionRow icon="edit" label="Edit card" description="Fix a typo or change the answer." onClick={() => setSheet('edit')} />
            <ActionRow
              icon="flag"
              label={current.card.flag ? 'Remove flag' : 'Flag card'}
              description="Mark it to look at later. Flagged cards can be practiced together."
              onClick={() => act('flag')}
            />
            <ActionRow icon="calendar" label="Skip until tomorrow" description="Hide it for the rest of today." onClick={() => act('bury')} />
            <ActionRow
              icon="pause"
              label="Pause card"
              description="It won’t come up again until you resume it from Browse."
              onClick={() => act('suspend')}
            />
          </ActionList>
        ) : null}
        <ActionList>
          <ActionRow icon="help" label="How studying works" description="What each answer button does." to="/guide#study" />
        </ActionList>
      </Sheet>

      <Sheet open={sheet === 'edit'} onClose={() => setSheet(null)} title="Edit card" wide>
        {current ? (
          <NoteEditor
            noteId={current.note.id}
            embedded
            onDone={async (deleted) => {
              setSheet(null);
              if (deleted) {
                skip.current.add(current.card.id);
                customQueue.current?.shift();
                await loadNext();
              } else {
                const card = await db.cards.get(current.card.id);
                if (card && !card.deletedAt) {
                  const keepPhase = phase;
                  await show(card, current.config);
                  setPhase(keepPhase);
                } else {
                  await loadNext();
                }
              }
            }}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
